/**
 * customerActivity — lightweight client logger that writes events to the
 * `customer_activity` table. Safe to call without an authenticated session
 * (RLS allows anonymous inserts where user_id is null).
 *
 * Events are buffered and flushed in microtask batches to avoid blocking
 * UI work. All failures are swallowed — analytics must never crash the app.
 */
import { supabase } from "@/integrations/supabase/client";
import { getCurrentTenantId } from "@/lib/tenantContext";

const SESSION_KEY = "khays_activity_session_id";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "s_anon";
  }
}

export type ActivityEventType =
  | "page_view"
  | "product_view"
  | "favorite_add"
  | "favorite_remove"
  | "cart_add"
  | "cart_remove"
  | "checkout_attempt"
  | "checkout_success"
  | "search"
  | "signup"
  | "login"
  | "logout"
  | "auth_signup_url_landing";

interface QueueItem {
  user_id: string | null;
  tenant_id: string | null;
  session_id: string;
  event_type: string;
  payload: Record<string, any>;
  user_agent: string | null;
  url: string;
}

let queue: QueueItem[] = [];
let flushScheduled = false;
let lastUserId: string | null = null;

async function resolveUserId(): Promise<string | null> {
  if (lastUserId !== null) return lastUserId;
  try {
    const { data } = await supabase.auth.getSession();
    lastUserId = data.session?.user?.id ?? null;
  } catch {
    lastUserId = null;
  }
  return lastUserId;
}

// Reset cached user when auth state flips
try {
  supabase.auth.onAuthStateChange((_e, sess) => {
    lastUserId = sess?.user?.id ?? null;
  });
} catch { /* SSR safe */ }

function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  setTimeout(flush, 1500);
}

async function flush() {
  flushScheduled = false;
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  try {
    await supabase.from("customer_activity").insert(batch as any);
  } catch (e) {
    // Swallow — analytics must never crash the app.
    console.debug("activity flush failed", e);
  }
}

/** Public API: enqueue an event. */
export async function logCustomerActivity(
  eventType: ActivityEventType | string,
  payload: Record<string, any> = {},
  opts: { immediate?: boolean } = {}
): Promise<void> {
  try {
    const user_id = await resolveUserId();
    const item: QueueItem = {
      user_id,
      tenant_id: getCurrentTenantId(),
      session_id: getSessionId(),
      event_type: eventType,
      payload,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      url: typeof window !== "undefined" ? window.location.href : "",
    };
    queue.push(item);
    if (opts.immediate) {
      await flush();
    } else {
      scheduleFlush();
    }
  } catch {
    /* noop */
  }
}

/** Flush remaining events on page unload using sendBeacon-friendly path. */
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    try { flush(); } catch {}
  });
}
